import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAppSelector } from "@store/index";
import { selectUser } from "@features/auth/authSlice";
import { documentService } from "@/services/document.service";
import { mainlineApi } from "@/services/drbfm.mainline";
import type { DocumentItem } from "../document-selector/documentItem";
import type { DocumentSourceSlotProps } from "./types";

type Preview = Awaited<ReturnType<typeof mainlineApi.document>>;
const PAGE_SIZE = 50;

export function useRegisteredDocumentSource({
  role,
  value,
  onChange,
  onEditCopy,
  disabled,
}: DocumentSourceSlotProps) {
  const user = useAppSelector(selectUser);
  const session = sessionStorage.getItem("auth_session_id");
  const scope = `${user?.tenantId}:${user?.id}:${session}`;
  const key = JSON.stringify([
    scope,
    role,
    value?.documentId,
    value?.versionId,
    disabled,
  ]);
  const current = useRef(key);
  useLayoutEffect(() => {
    current.current = key;
  }, [key]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<{ key: string; data: Preview } | null>(
    null,
  );
  const latestPreview = useRef(preview);
  useLayoutEffect(() => {
    latestPreview.current = preview;
  }, [preview]);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const runs = useRef({ search: 0, selection: 0, preview: 0 }).current;
  const page = useRef(0);
  const searchedQuery = useRef("");
  const listingScope = useRef(scope);
  const sameSession = useCallback(
    () => sessionStorage.getItem("auth_session_id") === session,
    [session],
  );

  const changeOpen = useCallback((next: boolean) => {
    runs.search++;
    runs.selection++;
    setBusy(next);
    setSelecting(false);
    setError(null);
    if (next) {
      setQuery("");
      setDocuments([]);
      setTotal(0);
    }
    setOpen(next);
  }, [runs]);

  useEffect(() => {
    changeOpen(false);
    setDocuments([]);
    setTotal(0);
    return () => {
      runs.search++;
      runs.selection++;
      runs.preview++;
    };
  }, [key, disabled, changeOpen, runs]);

  useEffect(() => {
    const documentId = value?.documentId;
    const versionId = value?.versionId;
    const refresh = async () => {
      const run = ++runs.preview;
      setPreview(null);
      setPreviewError(null);
      if (!documentId) return;
      try {
        const result = await mainlineApi.document(documentId);
        if (
          run !== runs.preview ||
          current.current !== key ||
          !sameSession()
        )
          return;
        if (result.version_id !== versionId)
          throw new Error("source_version_changed");
        setPreview({ key, data: result });
      } catch (cause) {
        if (
          run === runs.preview &&
          current.current === key &&
          sameSession()
        )
          setPreviewError(cause);
      }
    };
    const visible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    void refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      runs.preview++;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [key, value?.documentId, value?.versionId, sameSession, runs]);

  const search = useCallback(
    async (more = false, text = searchedQuery.current) => {
      const run = ++runs.search;
      const requestKey = current.current;
      setBusy(true);
      setError(null);
      const nextPage = more ? page.current + 1 : 1;
      if (!more) {
        setDocuments([]);
        setTotal(0);
        searchedQuery.current = text;
      }
      try {
        const result = await documentService.getDocuments({
          search: text,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        if (
          run !== runs.search ||
          current.current !== requestKey ||
          !sameSession()
        )
          return;
        listingScope.current = scope;
        page.current = nextPage;
        setDocuments((previous) => (more ? [...previous, ...result.results] : result.results));
        setTotal(result.total);
      } catch (cause) {
        if (
          run === runs.search &&
          current.current === requestKey &&
          sameSession()
        )
          setError(cause);
      } finally {
        if (run === runs.search) setBusy(false);
      }
    },
    [scope, sameSession, runs],
  );

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      void search(false, query);
    }, 300);
    return () => {
      window.clearTimeout(timer);
      runs.search++;
    };
  }, [open, query, search, runs]);

  const changeQuery = (text: string) => {
    runs.search++;
    runs.selection++;
    setSelecting(false);
    setDocuments([]);
    setTotal(0);
    setBusy(true);
    setQuery(text);
  };
  const select = async (doc: DocumentItem) => {
    if (disabled) return;
    const run = ++runs.selection;
    const requestKey = key;
    setSelecting(true);
    setError(null);
    try {
      const result = await mainlineApi.document(doc.id);
      if (
        run !== runs.selection ||
        current.current !== requestKey ||
        !sameSession()
      )
        return;
      changeOpen(false);
      onChange({
        documentId: result.document_id,
        versionId: result.version_id,
        title: result.title,
        fileName: result.file_name,
      });
    } catch (cause) {
      if (
        run === runs.selection &&
        current.current === requestKey &&
        sameSession()
      )
        setError(cause);
    } finally {
      if (run === runs.selection) setSelecting(false);
    }
  };
  const canCopyPreview =
    !!onEditCopy &&
    !disabled &&
    !!value?.versionId &&
    preview?.key === key &&
    current.current === key &&
    sameSession();
  const copyPreview = () => {
    if (
      !canCopyPreview ||
      !preview ||
      latestPreview.current !== preview ||
      current.current !== key ||
      !sameSession()
    )
      return;
    const source = preview.data;
    onEditCopy?.(
      {
        documentId: source.document_id,
        versionId: source.version_id,
        title: source.title,
        fileName: source.file_name,
      },
      source.content,
    );
  };
  return {
    canCopyPreview,
    copyPreview,
    open,
    changeOpen,
    query,
    changeQuery,
    documents: listingScope.current === scope ? documents : [],
    total,
    busy,
    selecting,
    error,
    preview: preview?.key === key ? preview.data : null,
    previewError,
    search,
    select,
  };
}
